import * as React from "react";
import { Form as FormikForm, Formik, FormikHelpers } from "formik";
import { Alert, Button, CircularProgress, Stack } from "@mui/material";
import { SettingsSnapshot } from "bloggers-backend/lib/settings/domain";
import { AppContext } from "../../../AppContext";
import { useUpdateSettings, useValidateSettings } from "../hooks/useSettings";
import { readDomains } from "../readDomains";
import { toFieldErrors } from "../toFieldErrors";
import { SettingsCard } from "./SettingsCard";

type SettingsValues = Record<string, unknown>;

interface Props {
  snapshot: SettingsSnapshot;
}

export const SettingsForm = ({snapshot}: Props) => {
  const {notificationsService} = React.useContext(AppContext);
  const updateSettings = useUpdateSettings();
  const validateSettings = useValidateSettings();
  const [validationMessage, setValidationMessage] = React.useState<string | undefined>();
  const domains = readDomains(snapshot.schema);

  const validate = async (values: SettingsValues) => {
    const result = await validateSettings(values);
    if (result.success || result.reason !== "validation") {
      setValidationMessage(undefined);
      return {};
    }
    const {fieldErrors, notificationMessage} = toFieldErrors(result.errors);
    setValidationMessage(notificationMessage);
    return fieldErrors;
  };

  const save = async (values: SettingsValues, form: FormikHelpers<SettingsValues>) => {
    const result = await updateSettings.mutateAsync(values);
    if (result.success) {
      notificationsService.notify({severity: "success", message: "Settings saved"});
      form.resetForm({values: result.value});
      return;
    }
    const {fieldErrors, notificationMessage} = toFieldErrors(result.errors);
    form.setErrors(fieldErrors);
    notificationsService.notify({severity: "error", message: notificationMessage ?? result.reason});
  };

  return (
    <Formik initialValues={snapshot.values} onSubmit={save} validate={validate} enableReinitialize>
      <FormikForm>
        <Stack spacing={3} sx={{width: "100%", maxWidth: 432}}>
          {domains.map((domain) =>
            <SettingsCard
              key={domain.key}
              domainKey={domain.key}
              description={domain.description}
              jsonSchema={domain.jsonSchema}
            />)}
          {validationMessage ? <Alert severity="error">{validationMessage}</Alert> : null}
          {updateSettings.isLoading
            ? <CircularProgress/>
            : <Button variant="contained" type="submit" sx={{alignSelf: "flex-start"}}>Save</Button>}
        </Stack>
      </FormikForm>
    </Formik>
  );
};
